import type React from 'react';

import { VisibilityRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useGetOne, useTranslate } from 'react-admin';

import { isoToDateTime } from 'src/lib/timeUtils';

import type { CallSession } from '@ragu-components/ord-types';

interface CallSessionInfoProps {
  callSessionId: string;
  showAdvanced: boolean;
  setShowAdvanced: (value: boolean) => void;
}

/**
 * Displays summary information about the call session.
 */
export const CallSessionInfo = ({
  callSessionId,
  showAdvanced,
  setShowAdvanced,
}: CallSessionInfoProps): React.ReactElement => {
  const translate = useTranslate();
  const { data: callSession, isLoading, error } = useGetOne<CallSession>('call_sessions', { id: callSessionId });

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', mb: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error != null || callSession === undefined) {
    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        {translate('resources.call_sessions.errors.loadFailed', { _: 'Unable to load call session details.' })}
      </Alert>
    );
  }

  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap' justifyContent='space-between'>
            <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
              <Typography variant='h6' component='h2'>
                {translate('resources.call_sessions.info.title', { _: 'Call Session Details' })}
              </Typography>
              {callSession.hangup_cause !== null && callSession.hangup_cause !== undefined && (
                <Chip
                  label={translate(`resources.call_sessions.fields.hangupCauses.${callSession.hangup_cause}`, {
                    _: callSession.hangup_cause,
                  })}
                  size='small'
                  color='default'
                  variant='outlined'
                />
              )}
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvanced}
                  onChange={(e) => {
                    setShowAdvanced(e.target.checked);
                  }}
                  size='small'
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityRounded fontSize='small' />
                  <Typography variant='body2'>
                    {translate('resources.call_session_events.toggleAdvanced', { _: 'Show advanced info' })}
                  </Typography>
                </Box>
              }
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap='wrap'>
            {callSession.caller_id_number !== null && callSession.caller_id_number !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.caller_id_number', { _: 'Caller' })}
                </Typography>
                <Typography variant='body2'>
                  {callSession.caller_id_name !== null && callSession.caller_id_name !== undefined
                    ? `${callSession.caller_id_name} (${callSession.caller_id_number})`
                    : callSession.caller_id_number}
                </Typography>
              </Box>
            )}

            {callSession.called_id_number !== null && callSession.called_id_number !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.called_id_number', { _: 'Called' })}
                </Typography>
                <Typography variant='body2'>
                  {callSession.called_id_name !== null && callSession.called_id_name !== undefined
                    ? `${callSession.called_id_name} (${callSession.called_id_number})`
                    : callSession.called_id_number}
                </Typography>
              </Box>
            )}

            {callSession.account !== null && callSession.account !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.account', { _: 'Account' })}
                </Typography>
                <Typography variant='body2'>{callSession.account}</Typography>
              </Box>
            )}

            {callSession.called_account !== null && callSession.called_account !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.called_account', { _: 'Called Account' })}
                </Typography>
                <Typography variant='body2'>{callSession.called_account}</Typography>
              </Box>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap='wrap'>
            {callSession.creation_time !== null && callSession.creation_time !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.creation_time', { _: 'Created' })}
                </Typography>
                <Typography variant='body2'>{isoToDateTime(callSession.creation_time)}</Typography>
              </Box>
            )}

            {callSession.answer_time !== null && callSession.answer_time !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.answer_time', { _: 'Answered' })}
                </Typography>
                <Typography variant='body2'>{isoToDateTime(callSession.answer_time)}</Typography>
              </Box>
            )}

            {callSession.hangup_time !== null && callSession.hangup_time !== undefined && (
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  {translate('resources.call_sessions.fields.hangup_time', { _: 'Hangup' })}
                </Typography>
                <Typography variant='body2'>{isoToDateTime(callSession.hangup_time)}</Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CallSessionInfo;
